import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createCashfreeSubscription,
  createPhonePeSubscription,
  verifyCashfreeSubscription,
  verifyPhonePeSubscription,
  cancelCoupon,
} from '@/lib/api';
import { toast } from 'sonner';
import * as Sentry from '@sentry/nextjs';
import { trackAnalyticsEvent } from '@/lib/analytics';
import type { UtmTrackingParams } from '@/lib/url-utils';
import { UPI_PACKAGE_NAMES, type PaymentApp, type PaymentMethod } from '@/constants/payment';

type PaymentProvider = 'phonepe' | 'cashfree';

const resolvePhonePeApp = (upiApp?: string): PaymentApp => {
  if (upiApp === 'GPAY' || upiApp === 'PAYTM' || upiApp === 'PHONEPE') {
    return upiApp;
  }
  return 'PHONEPE';
};

export const useSubscriptionPayment = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [timer, setTimer] = useState(300);
  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'failure'>('idle');
  const [failureReason, setFailureReason] = useState<string | null>(null);
  const [responseCode, setResponseCode] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);
  const successCbRef = useRef<(() => void) | null>(null);
  const planDataRef = useRef<any>(null);
  const userDataRef = useRef<any>(null);
  const paymentDataRef = useRef<any>(null);
  const couponCodeRef = useRef<string | null>(null);
  const utmTrackingParamsRef = useRef<UtmTrackingParams>({});
  const providerRef = useRef<PaymentProvider | null>(null);

  const clearPoll = () => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    if (status === 'pending' && subscriptionId) {
      setTimer(300);
      pollRef.current = window.setInterval(async () => {
        setTimer((prev) => {
          if (prev <= 0) {
            clearPoll();
            setStatus('failure');
            setFailureReason('TIMEOUT');
            setResponseCode('TIMEOUT');
            setIsProcessing(false);

            if (couponCodeRef.current) {
              cancelCoupon(couponCodeRef.current).catch((err) => {
                console.error('Failed to cancel coupon on timeout', err);
                Sentry.captureException(err, { tags: { section: 'coupon_cancel_timeout' } });
              });
            }

            toast.error('Payment verification timeout');
            trackAnalyticsEvent('PAYMENT_ERROR', {
              provider: providerRef.current || 'unknown',
              subscriptionId,
              reason: 'TIMEOUT',
              ...utmTrackingParamsRef.current,
            });
            return 0;
          }
          return prev;
        });

        try {
          const provider = providerRef.current;
          const res = provider === 'phonepe'
            ? await verifyPhonePeSubscription(subscriptionId)
            : await verifyCashfreeSubscription(subscriptionId);

          if (res.subscriptionAuthStatus === 'SUCCESS') {
            paymentDataRef.current = res;
            setStatus('success');
            setIsProcessing(false);
            clearPoll();
            try { successCbRef.current && successCbRef.current(); } catch (e) { console.error(e); }
          } else if (res.subscriptionAuthStatus === 'FAILURE') {
            const reason = provider === 'phonepe'
              ? ('errorCode' in res ? res.errorCode : undefined)
              : ('failure_reason' in res ? res.failure_reason : undefined);
            const code = provider === 'phonepe'
              ? ('errorCode' in res ? res.errorCode : undefined)
              : ('response_code' in res ? res.response_code : undefined);

            setStatus('failure');
            setFailureReason(reason || 'FAILURE');
            setResponseCode(code || '400');
            setIsProcessing(false);
            clearPoll();

            if (couponCodeRef.current) {
              cancelCoupon(couponCodeRef.current).catch((err) => {
                console.error('Failed to cancel coupon on failure', err);
                Sentry.captureException(err, { tags: { section: 'coupon_cancel_failure' } });
              });
            }

            trackAnalyticsEvent('PAYMENT_ERROR', {
              provider: provider || 'unknown',
              subscriptionId,
              reason: reason || 'FAILURE',
              ...utmTrackingParamsRef.current,
            });
            toast.error('Payment Failed. Please try again.');
          }
        } catch (err: any) {
          const provider = providerRef.current || 'unknown';
          console.error(`${provider} polling error`, err);
          Sentry.captureException(err, { tags: { section: `${provider}_poll` } });
        }
      }, 3000) as unknown as number;
    }
    return () => clearPoll();
  }, [status, subscriptionId]);

  useEffect(() => {
    let t: number | undefined;
    if (status === 'pending') {
      t = window.setInterval(() => setTimer((prev: number) => Math.max(0, prev - 1)), 1000) as unknown as number;
    }
    return () => { if (t) window.clearInterval(t); };
  }, [status]);

  const cancel = useCallback(async (shouldCancelCoupon = false) => {
    clearPoll();

    if (shouldCancelCoupon && couponCodeRef.current) {
      try {
        await cancelCoupon(couponCodeRef.current);
      } catch (err) {
        console.error('Failed to cancel coupon', err);
        Sentry.captureException(err, { tags: { section: 'coupon_cancel_manual' } });
      }
    }

    setIsProcessing(false);
    setSubscriptionId(null);
    setStatus('idle');
    setTimer(300);
    setFailureReason(null);
    setResponseCode(null);
    couponCodeRef.current = null;
    utmTrackingParamsRef.current = {};
    providerRef.current = null;
  }, []);

  interface StartOptions {
    plan: any;
    user?: { _id?: string; fullName?: string; email?: string; phoneNumber?: string };
    method: 'APP' | 'VPA';
    upiApp?: PaymentMethod;
    upiId?: string;
    couponCode?: string;
    sourcePath?: string;
    utmTrackingParams?: UtmTrackingParams;
    onSuccess?: () => void;
    onError?: (err: any) => void;
  }

  const startPayment = useCallback(async (opts: StartOptions) => {
    if (!opts.plan?._id) return { ok: false };

    const provider: PaymentProvider = opts.method === 'VPA' ? 'cashfree' : 'phonepe';
    setIsProcessing(true);
    setStatus('idle');
    setFailureReason(null);
    setResponseCode(null);
    providerRef.current = provider;
    utmTrackingParamsRef.current = opts.utmTrackingParams || {};

    trackAnalyticsEvent('PAYMENT_INITIATED', {
      provider,
      plan_name: opts.plan?.name,
      plan_id: opts.plan?._id,
      price: opts.plan?.price,
      user_id: opts.user?._id,
      ...opts.utmTrackingParams,
    });

    try {
      const commonPayload = {
        premiumPlanId: opts.plan._id,
        ...(opts.couponCode ? { couponCode: opts.couponCode } : {}),
        ...(opts.sourcePath ? { sourcePath: opts.sourcePath } : {}),
      };

      let subId: string | null = null;
      const response = provider === 'phonepe'
        ? await createPhonePeSubscription({
          ...commonPayload,
          upiApp: resolvePhonePeApp(opts.upiApp),
          upiPackageName: UPI_PACKAGE_NAMES[resolvePhonePeApp(opts.upiApp)],
        })
        : await createCashfreeSubscription({
          ...commonPayload,
          upiId: opts.upiId,
        });

      if (response.status) {
        if (provider === 'phonepe') {
          const phonePeResponse = response as Awaited<ReturnType<typeof createPhonePeSubscription>>;
          subId = phonePeResponse.subscription?.idForPolling || phonePeResponse.subscription?.subscriptionId || null;
        } else {
          const cashfreeResponse = response as Awaited<ReturnType<typeof createCashfreeSubscription>>;
          subId = cashfreeResponse.subscription?.id || null;
        }

        if (!subId) {
          toast.error(response.message || 'Failed to initiate payment');
          setIsProcessing(false);
          if (opts.onError) opts.onError(response);
          return { ok: false };
        }

        setSubscriptionId(subId);
        planDataRef.current = opts.plan;
        userDataRef.current = opts.user;
        couponCodeRef.current = opts.couponCode || null;
        utmTrackingParamsRef.current = opts.utmTrackingParams || {};
        setStatus('pending');

        if (response.upiPaymentLinks) {
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
          if (isIOS && response.upiPaymentLinks.ios) {
            window.location.href = response.upiPaymentLinks.ios;
          } else if (response.upiPaymentLinks.android) {
            window.location.href = response.upiPaymentLinks.android;
          } else if (response.upiPaymentLinks.web) {
            window.location.href = response.upiPaymentLinks.web;
          }
        }

        if (opts.onSuccess) successCbRef.current = opts.onSuccess;
        return { ok: true, subscriptionId: subId };
      }

      trackAnalyticsEvent('PAYMENT_ERROR', {
        provider,
        reason: 'INIT_FAILED',
        message: response.message || '',
        ...opts.utmTrackingParams,
      });
      toast.error(response.message || 'Failed to initiate payment');
      setIsProcessing(false);
      if (opts.onError) opts.onError(response);
      return { ok: false };
    } catch (error: any) {
      Sentry.captureException(error, {
        tags: { section: `${provider}_start` },
        extra: {
          status: error?.response?.status,
          url: error?.config?.url,
          method: error?.config?.method,
          requestPayload: {
            premiumPlanId: opts.plan?._id,
            upiApp: opts.upiApp,
            hasUpiId: !!opts.upiId,
            hasCoupon: !!opts.couponCode,
          },
          responseData: error?.response?.data,
          browser: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        },
      });
      console.error('startPayment error', error);
      toast.error('Something went wrong');
      setIsProcessing(false);
      utmTrackingParamsRef.current = opts.utmTrackingParams || {};
      if (opts.onError) opts.onError(error);
      return { ok: false };
    }
  }, []);

  return {
    startPayment,
    cancel,
    isProcessing,
    subscriptionId,
    timer,
    status,
    failureReason,
    responseCode,
  };
};
